import { api } from '@/lib/api/client';
import type {
  Attendance,
  AttendanceFilters,
  AttendancesResponse,
  ArriveDto,
  LeaveDto,
} from '../types';

const ATTENDEES_URL = '/attendees';

export const attendancesApi = {
  // Get all attendances with filters
  getAll: async (filters?: AttendanceFilters): Promise<AttendancesResponse> => {
    const params: Record<string, unknown> = { ...filters };

    if (params['page'] !== undefined && params['limit']) {
      params['offset'] = (params['page'] as number) * (params['limit'] as number);
      delete params['page'];
    }

    // Handle search - only send if 2+ chars
    if (params['s'] && (params['s'] as string).length < 2) {
      delete params['s'];
    }

    // Handle date filters
    if (params['createdAt.min']) {
      const minDate = new Date(params['createdAt.min'] as string);
      minDate.setHours(0, 0, 0, 0);
      params['createdAt.min'] = minDate.toISOString();
    }

    if (params['createdAt.max']) {
      const maxDate = new Date(params['createdAt.max'] as string);
      maxDate.setHours(23, 59, 59, 999);
      params['createdAt.max'] = maxDate.toISOString();
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
      if (params[key] == null || params[key] === '') {
        delete params[key];
      }
    });

    // Remove order params if empty
    if (!params['orderBy'] || !params['order']) {
      delete params['orderBy'];
      delete params['order'];
    }

    return api.get<AttendancesResponse>(ATTENDEES_URL, { params });
  },

  // Get single attendance by ID
  getById: async (id: number | string): Promise<Attendance> => {
    return api.get<Attendance>(`${ATTENDEES_URL}/${id}`);
  },

  // Check if user is currently inside (active attendance)
  getActive: async (): Promise<Attendance | null> => {
    try {
      return await api.get<Attendance>(`${ATTENDEES_URL}/active`);
    } catch {
      return null;
    }
  },

  // Arrive
  arrive: async (data: ArriveDto): Promise<Attendance> => {
    return api.post<Attendance>(`${ATTENDEES_URL}/arrive`, data);
  },

  // Leave
  leave: async (data: LeaveDto): Promise<Attendance> => {
    return api.post<Attendance>(`${ATTENDEES_URL}/leave`, data);
  },
};

// Query keys for React Query
export const attendancesQueryKeys = {
  all: ['attendances'] as const,
  lists: () => [...attendancesQueryKeys.all, 'list'] as const,
  list: (filters?: AttendanceFilters) => [...attendancesQueryKeys.lists(), filters] as const,
  details: () => [...attendancesQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...attendancesQueryKeys.details(), id] as const,
  active: () => [...attendancesQueryKeys.all, 'active'] as const,
};
